import React from 'react';

const META = {
	version:      '1.0',
	lastUpdated:  'April 25, 2026',
	effective:    'May 1, 2026',
	privacyEmail: 'privacy@justknow.co.in',
	supportEmail: 'support@justknow.co.in',
	altEmail:     'contact@justknow.co.in',
	dpoName:      'JGS Srinivasa Rao',
	company:      'JGS Media And Software Solutions Pvt Ltd',
	address:      'Dr.No 9-10-184, 23rd Ward, Ramakrishna Colony, Tuni, Andhra Pradesh - 533401',
};

const PrivacyPolicyPage: React.FC = () => (
	<div style={{
		position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
		overflowY: 'auto', background: '#0a0a1a',
		fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		color: '#fff',
		zIndex: 9000,
	}}>

		{/* Nav */}
		<nav style={{
			position: 'sticky', top: 0, zIndex: 100,
			background: 'rgba(10,10,26,0.9)', backdropFilter: 'blur(20px)',
			borderBottom: '1px solid rgba(255,255,255,0.06)',
			padding: '0 24px',
		}}>
			<div style={{ maxWidth: 800, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<a href='/'>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 32, objectFit: 'contain' }} />
				</a>
				<a href='/' style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, textDecoration: 'none' }}>← Back to Home</a>
			</div>
		</nav>

		{/* Content */}
		<div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 24px 80px' }}>

			{/* Header */}
			<div style={{ marginBottom: 48 }}>
				<h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', marginBottom: 12 }}>Privacy Policy</h1>
				<p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
					v{META.version} &nbsp;·&nbsp; Effective {META.effective} &nbsp;·&nbsp; Last updated {META.lastUpdated}
				</p>
				<p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.7, marginTop: 20 }}>
					Justknow ("we," "our," or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information in compliance with the Digital Personal Data Protection Act (DPDPA), 2023, and the Information Technology Act, 2000.
				</p>
				<p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.7, marginTop: 12 }}>
					By using the Justknow mobile application ("App"), you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our App.
				</p>
				<p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginTop: 12 }}>
					Compliant with DPDPA 2023 · IT Act 2000 · IT Rules 2021
				</p>
			</div>

			<Divider />

			{/* Section 1 */}
			<Section title="1. Data Fiduciary Information">
				<p>As required under DPDPA 2023, we provide our contact information:</p>
				<InfoBox>
					<p><strong style={{ color: '#fff' }}>Justknow</strong></p>
					<p style={{ marginTop: 8 }}>Registered Office: {META.company}, {META.address}</p>
					<p>Email: <a href={`mailto:${META.privacyEmail}`} style={{ color: '#4d69fa' }}>{META.privacyEmail}</a></p>
					<p>Data Protection Officer: {META.dpoName}</p>
					<p>DPO Email: <a href={`mailto:${META.supportEmail}`} style={{ color: '#4d69fa' }}>{META.supportEmail}</a></p>
				</InfoBox>
			</Section>

			{/* Section 2 */}
			<Section title="2. Personal Data We Collect">
				<SubHeading>2.1 Information You Provide Directly</SubHeading>
				<p>When you create an account, we collect:</p>
				<ul>
					<li><b>Name</b> — first and last name for account identification.</li>
					<li><b>Email Address</b> — for authentication and communication.</li>
					<li><b>Password</b> — securely hashed and encrypted; never stored in plain text.</li>
					<li><b>Phone Number</b> — (optional) for account verification.</li>
					<li><b>Profile Photo</b> — (optional) for account personalisation.</li>
				</ul>

				<SubHeading>2.2 Information Collected Automatically</SubHeading>
				<p>When you use our app, we automatically collect certain information to help us improve your experience, ensure the app works correctly, and keep our services secure. This includes general information about your device, how you use the app, and data needed to diagnose and resolve any issues you may encounter.</p>

				<SubHeading>2.3 Location Information</SubHeading>
				<p>With your permission, we may collect your location to deliver news that is relevant to your region. You can withdraw this permission at any time through your device settings, and doing so will not affect your ability to use the app.</p>

				<SubHeading>2.4 Push Notification Data</SubHeading>
				<p>We may collect information necessary to send you news alerts and updates based on your notification preferences. This includes your subscription choices and how you interact with the notifications you receive.</p>
			</Section>

			{/* Section 3 */}
			<Section title="3. How We Use Your Personal Data">
				<p>We use your data for the following legitimate purposes:</p>

				<SubHeading>3.1 Essential Services (Contractual Necessity)</SubHeading>
				<ul>
					<li>User authentication and account management.</li>
					<li>Delivering news content you request.</li>
					<li>Processing your favourites and bookmarks.</li>
					<li>Sending transactional notifications (password resets, security alerts).</li>
				</ul>

				<SubHeading>3.2 Content Personalisation (With Your Consent)</SubHeading>
				<ul>
					<li>Recommending articles based on your reading history.</li>
					<li>Providing location-based regional news.</li>
					<li>Customising home feed based on preferences.</li>
					<li>Language and category personalisation.</li>
				</ul>

				<SubHeading>3.3 Analytics &amp; Product Improvement (Legitimate Interest)</SubHeading>
				<ul>
					<li>Understanding how users interact with content.</li>
					<li>Improving app performance and user experience.</li>
					<li>Identifying and fixing technical issues.</li>
					<li>Testing new features and optimisations.</li>
				</ul>

				<SubHeading>3.4 Security &amp; Fraud Prevention (Legal Obligation)</SubHeading>
				<ul>
					<li>Detecting and preventing unauthorised access.</li>
					<li>Identifying suspicious or fraudulent activity.</li>
					<li>Enforcing our Terms and Conditions.</li>
					<li>Complying with legal obligations and court orders.</li>
				</ul>

				<SubHeading>3.5 Communications (With Your Consent)</SubHeading>
				<ul>
					<li>Sending you push notifications about breaking news.</li>
					<li>Delivering newsletters and content digests.</li>
					<li>Informing you about app updates and new features.</li>
					<li>Responding to your support inquiries.</li>
				</ul>

				<p>We do not use your data for automated decision-making that produces legal or significant effects on you.</p>
			</Section>

			{/* Section 4 */}
			<Section title="4. How We Share Your Data">
				<p><strong style={{ color: '#fff' }}>We do NOT sell your personal data to third parties.</strong> We only share data as described below:</p>

				<SubHeading>4.1 Third-Party Service Providers</SubHeading>
				<p>To deliver our services, we work with trusted third-party partners who assist us with functions such as content delivery, notifications, and advertising. These partners are carefully selected and are only permitted to use your information to the extent necessary to perform the services they provide on our behalf. We do not sell your personal data to any third party. All partners are required to handle your information responsibly and in accordance with applicable privacy laws.</p>

				<SubHeading>4.2 Legal Requirements</SubHeading>
				<p>We may disclose your data if required by law, court order, or government authority, or to:</p>
				<ul>
					<li>Comply with legal processes.</li>
					<li>Protect our rights, property, or safety.</li>
					<li>Prevent fraud or illegal activities.</li>
					<li>Enforce our Terms and Conditions.</li>
				</ul>

				<SubHeading>4.3 Business Transfers</SubHeading>
				<p>In the event of a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity. We will notify you before your data is transferred and becomes subject to a different privacy policy.</p>
			</Section>

			{/* Section 5 */}
			<Section title="5. Data Retention Policy">
				<p>We retain your personal data only as long as necessary for the purposes outlined in this policy or as required by law.</p>
				<p>We keep your data only for as long as it is needed to provide you with our services or to meet our legal obligations. Once that period has passed, your data is securely deleted or anonymised so that it can no longer be linked back to you.</p>
			</Section>

			{/* Section 6 */}
			<Section title="6. Your Rights Under DPDPA 2023">
				<p>You have the following rights regarding your personal data:</p>

				<SubHeading>6.1 Right to Access</SubHeading>
				<p>Request a copy of all personal data we hold about you. We will provide this within 30 days.</p>

				<SubHeading>6.2 Right to Correction</SubHeading>
				<p>Request correction of inaccurate or incomplete data. You can update most information in your Account Settings.</p>

				<SubHeading>6.3 Right to Erasure (Right to Be Forgotten)</SubHeading>
				<p>Request deletion of your personal data. We will delete your data within 30 days unless required by law to retain it.</p>

				<SubHeading>6.4 Right to Data Portability</SubHeading>
				<p>Request your data in a structured, machine-readable format (JSON/CSV). You can transfer this data to another service.</p>

				<SubHeading>6.5 Right to Withdraw Consent</SubHeading>
				<p>Withdraw consent for data processing at any time. This does not affect processing done before withdrawal.</p>

				<SubHeading>6.6 Right to Grievance Redressal</SubHeading>
				<p>File a complaint if you believe your rights have been violated. We will respond within 48 hours.</p>

				<InfoBox highlight>
					<p style={{ fontWeight: 700, textAlign: 'center', marginBottom: 8, color: '#4d69fa' }}>How to Exercise Your Rights</p>
					<p style={{ textAlign: 'center' }}>
						Email: <a href={`mailto:${META.supportEmail}`} style={{ color: '#4d69fa' }}>{META.supportEmail}</a><br />
						Subject: [Data Rights Request]<br />
						Response Time: Within 30 days
					</p>
				</InfoBox>
			</Section>

			{/* Section 7 */}
			<Section title="7. Security Measures">
				<p>We take the security of your personal data seriously and have appropriate measures in place to protect it from unauthorised access, loss, or misuse. While we do everything reasonably practicable to keep your data safe, no system is completely immune to risk and we encourage you to use the app responsibly.</p>
			</Section>

			{/* Section 8 */}
			<Section title="8. Children's Privacy">
				<p>Our App is intended for users aged <strong style={{ color: '#fff' }}>18 and above</strong>. We do not knowingly collect personal data from children under 18 without parental consent.</p>
				<p>If you are a parent or guardian and believe your child has provided us with personal data, please contact us at <a href={`mailto:${META.privacyEmail}`} style={{ color: '#4d69fa' }}>{META.privacyEmail}</a>. We will delete such data within 72 hours.</p>
			</Section>

			{/* Section 9 */}
			<Section title="9. Cookies and Tracking Technologies">
				<p>We may use standard technologies to understand how you use our app and to support features such as personalised content and advertising. You can manage your preferences for tracking and personalisation at any time through your device settings.</p>
			</Section>

			{/* Section 10 */}
			<Section title="10. Cross-Border Data Transfer">
				<p>Some of our trusted service partners may be based outside India, which may require your data to be processed in other countries. Where this occurs, we ensure that your data continues to receive an appropriate level of protection in line with applicable privacy laws.</p>
			</Section>

			{/* Section 11 */}
			<Section title="11. Changes to This Privacy Policy">
				<p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
				<ul>
					<li>Posting the updated policy on the App with a new "Last Updated" date.</li>
					<li>Sending an in-app notification.</li>
					<li>Emailing you at the address associated with your account.</li>
					<li>Requiring re-consent for significant changes.</li>
				</ul>
				<p>Your continued use of the App after the effective date constitutes acceptance of the revised policy.</p>
			</Section>

			{/* Section 12 */}
			<Section title="12. Contact Us">
				<p>For any privacy-related questions, concerns, or requests:</p>
				<InfoBox>
					<p><strong style={{ color: '#4d69fa' }}>Data Protection Officer</strong></p>
					<p style={{ marginTop: 6 }}>Name: {META.dpoName}</p>
					<p>Email: <a href={`mailto:${META.supportEmail}`} style={{ color: '#4d69fa' }}>{META.supportEmail}</a></p>
					<p>Alternative: <a href={`mailto:${META.altEmail}`} style={{ color: '#4d69fa' }}>{META.altEmail}</a></p>
					<p>Address: {META.company}, {META.address}</p>
					<p>Response Time: Within 30 days</p>
				</InfoBox>
			</Section>

			<Divider />

			{/* Acknowledgment */}
			<InfoBox highlight>
				<p style={{ fontWeight: 700, textAlign: 'center', marginBottom: 8, color: '#4d69fa' }}>Your Privacy Matters</p>
				<p style={{ textAlign: 'center' }}>
					We are committed to being transparent about how we collect, use, and protect your data. This policy is designed to empower you with control over your personal information.
				</p>
			</InfoBox>

			{/* Footer contact */}
			<div style={{ textAlign: 'center', paddingTop: 32 }}>
				<p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
					Questions? Email us at{' '}
					<a href={`mailto:${META.privacyEmail}`} style={{ color: '#4d69fa' }}>{META.privacyEmail}</a>
				</p>
			</div>
		</div>

		{/* Footer */}
		<footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)', padding: '32px 24px' }}>
			<div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 16 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 26, objectFit: 'contain' }} />
					<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>Short News App</span>
				</div>
				<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
					© {new Date().getFullYear()} {META.company}. All rights reserved.
				</span>
			</div>
		</footer>

		<style>{`
			* { margin: 0; padding: 0; box-sizing: border-box; }
			::-webkit-scrollbar { width: 6px; }
			::-webkit-scrollbar-track { background: #0a0a1a; }
			::-webkit-scrollbar-thumb { background: rgba(108,93,211,0.5); border-radius: 3px; }
		`}</style>
	</div>
);

// ─── helpers ──────────────────────────────────────────────────────────────────

const Divider = () => (
	<hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '40px 0' }} />
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<p style={{ fontWeight: 700, color: '#fff', marginTop: 16, marginBottom: 6, fontSize: 14 }}>{children}</p>
);

const InfoBox: React.FC<{ children: React.ReactNode; highlight?: boolean }> = ({ children, highlight }) => (
	<div style={{
		background: highlight ? 'rgba(77,105,250,0.08)' : 'rgba(255,255,255,0.04)',
		border: `1px solid ${highlight ? 'rgba(77,105,250,0.2)' : 'rgba(255,255,255,0.07)'}`,
		borderRadius: 10,
		padding: '16px 20px',
		marginTop: 14,
		fontSize: 14,
		lineHeight: 1.8,
		color: 'rgba(255,255,255,0.6)',
	}}>
		{children}
	</div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div style={{ marginBottom: 40 }}>
		<h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: '#fff' }}>{title}</h2>
		<div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.8 }}>
			{children}
		</div>
		<style>{`
			div > div ul { padding-left: 20px; margin: 8px 0; }
			div > div li { margin-bottom: 8px; }
			div > div p  { margin-bottom: 12px; }
		`}</style>
	</div>
);

export default PrivacyPolicyPage;
